import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useLocale } from "../../hooks/useLocale";
import { formatDateTime, money } from "../../lib/format";
import { refreshHardware, useHardwareSnapshot } from "../../lib/hardwareStore";
import type { ConnectionTest, Discovery, DocumentPrinterSettings, HardwareConfig, PrinterConnection, PrinterSettings as Printer } from "../../lib/hardware";
import { Empty, Pill, SectionHeader } from "../ui/Badges";

type Kind = PrinterConnection["type"];
const KINDS: Array<{ kind: Kind; icon: string }> = [
  { kind: "spooler", icon: "🖨️" }, { kind: "network", icon: "🌐" }, { kind: "serial", icon: "📶" }, { kind: "usb", icon: "🔌" }, { kind: "none", icon: "⛔" },
];
const BAUDS = [9600, 19200, 38400, 57600, 115200];

const defaultFor = (kind: Kind, d: Discovery | null, prev: PrinterConnection): PrinterConnection => {
  if (kind === prev.type) return prev;
  switch (kind) {
    case "spooler": return { type: "spooler", printerName: d?.system.find((p) => p.isDefault)?.name ?? d?.system[0]?.name ?? "" };
    case "network": return { type: "network", host: "", port: 9100 };
    case "serial": return { type: "serial", path: d?.serial.find((p) => p.bluetooth)?.path ?? d?.serial[0]?.path ?? "", baudRate: 9600 };
    case "usb": return { type: "usb", vendorId: d?.usb[0]?.vendorId ?? 0, productId: d?.usb[0]?.productId ?? 0 };
    default: return { type: "none" };
  }
};

function Row({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="block"><span className="label">{label}</span>{children}{hint && <span className="mt-1 block text-[10px] font-medium text-gray-500">{hint}</span>}</label>;
}

/**
 * The hardware screen of the Windows POS: receipt printer (Windows printer, network IP, Bluetooth / COM port, raw USB), print quality, the A4 document
 * printer, tests that never waste paper, and the list of receipts that failed to print. Only the Electron app has these controls.
 */
export function PrinterSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const hw = useHardwareSnapshot();
  const bridge = window.hardware?.settings;
  const [cfg, setCfg] = useState<HardwareConfig | null>(null);
  const [disc, setDisc] = useState<Discovery | null>(null);
  const [test, setTest] = useState<ConnectionTest | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bridge) return;
    try {
      const [c, d] = await Promise.all([bridge.get(), bridge.discover()]);
      setCfg(c);
      setDisc(d);
    } catch (e) {
      toastError(e);
    }
  }, [bridge, toastError]);
  useEffect(() => { void load(); }, [load]);

  const guard = async <T,>(name: string, fn: () => Promise<T>, ok?: string): Promise<T | undefined> => {
    setBusy(name);
    try {
      const r = await fn();
      if (ok) toast(ok, "success");
      return r;
    } catch (e) {
      toastError(e);
      return undefined;
    } finally {
      setBusy(null);
      void refreshHardware();
    }
  };

  if (!bridge) return <section className="card p-5"><Empty>{t("hw.printer.browserOnly")}</Empty></section>;
  if (!cfg) return <section className="card p-5 text-xs font-bold text-gray-500">{t("common.loading")}</section>;

  const p = cfg.printer;
  const setPrinter = (patch: Partial<Printer>) => { setCfg({ ...cfg, printer: { ...p, ...patch } }); setTest(null); };
  const conn = p.connection;
  const setConn = (c: PrinterConnection) => setPrinter({ connection: c });
  const dis = !canEdit;
  const incomplete = (conn.type === "network" && !conn.host.trim()) || (conn.type === "spooler" && !conn.printerName) || (conn.type === "serial" && !conn.path) || (conn.type === "usb" && !conn.vendorId);

  const savePrinter = () => guard("save", () => bridge.save({ printer: p }).then((c) => setCfg(c)), t("hw.saved"));
  const doTest = () => guard("test", async () => setTest(await bridge.testConnection(conn)));
  const sample = (kind: "test-ar" | "test-en" | "diagnostic" | "drawer") => guard(kind, async () => { await bridge.save({ printer: p }); await bridge.printSample(kind); }, t(kind === "drawer" ? "hw.printer.drawerSent" : "hw.testSent"));

  const doc = cfg.documents;
  const setDoc = (patch: Partial<DocumentPrinterSettings>) => setCfg({ ...cfg, documents: { ...doc, ...patch } });
  const printDocTest = () => guard("doc", async () => {
    await bridge.save({ documents: doc });
    const html = `<!doctype html><html lang="${locale}" dir="${locale === "ar" ? "rtl" : "ltr"}"><head><meta charset="utf-8"><title>AstroPlanet</title><style>@page{size:${doc.paper};margin:15mm}body{font-family:Cairo,Arial,sans-serif;color:#0D0F2C}h1{color:#6B38FF}</style></head><body><h1>AstroPlanet — ${t("hw.doc.testTitle")}</h1><p>${t("hw.doc.testBody", { printer: doc.deviceName ?? t("hw.doc.defaultPrinter") })}</p><p dir="ltr">${new Date().toLocaleString("en-GB")}</p></body></html>`;
    await window.hardware!.printDocument!(html);
  }, t("hw.docSent"));

  return (
    <div className="space-y-5">
      <section className="card space-y-4 p-5" aria-label={t("hw.printer.title")}>
        <SectionHeader icon="🖨️" title={t("hw.printer.title")} subtitle={t("hw.printer.subtitle", { id: cfg.terminalId })}
          right={<div className="flex items-center gap-2">{hw.status && <StatusPill status={hw.status.state} />}<button type="button" className="btn-ghost" disabled={busy === "scan"} onClick={() => void guard("scan", load)}>🔄 {t("hw.printer.rescan")}</button></div>} />

        <div role="radiogroup" aria-label={t("hw.printer.connection")} className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
          {KINDS.map(({ kind, icon }) => (
            <label key={kind} className={`cursor-pointer rounded-2xl border-2 p-3 text-xs transition ${conn.type === kind ? "border-cosmic-purple bg-purple-50" : "border-gray-200 bg-gray-50 hover:border-gray-300"} ${dis ? "pointer-events-none opacity-60" : ""}`}>
              <input type="radio" name="connection" className="sr-only" checked={conn.type === kind} onChange={() => setConn(defaultFor(kind, disc, conn))} />
              <div className="text-lg" aria-hidden>{icon}</div>
              <div className="font-black">{t(`hw.printer.kind.${kind}`)}</div>
              <div className="mt-0.5 text-[10px] font-medium text-gray-500">{t(`hw.printer.kind.${kind}Hint`)}</div>
            </label>
          ))}
        </div>

        <fieldset disabled={dis} className="space-y-3">
          {conn.type === "spooler" && (
            <Row label={t("hw.printer.windowsPrinter")} hint={t("hw.printer.windowsHint")}>
              <select className="field" value={conn.printerName} onChange={(e) => setConn({ type: "spooler", printerName: e.target.value })}>
                <option value="">{t("hw.printer.choose")}</option>
                {(disc?.system ?? []).map((s) => <option key={s.name} value={s.name}>{s.name}{s.isDefault ? ` (${t("hw.printer.default")})` : ""}{s.looksBluetooth ? " 📶" : ""}</option>)}
                {conn.printerName && !(disc?.system ?? []).some((s) => s.name === conn.printerName) && <option value={conn.printerName}>{conn.printerName} ⚠️</option>}
              </select>
            </Row>
          )}
          {conn.type === "network" && (
            <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
              <Row label={t("hw.printer.host")} hint={t("hw.printer.hostHint")}><input className="field num" inputMode="decimal" placeholder="192.168.1.50" value={conn.host} onChange={(e) => setConn({ ...conn, host: e.target.value.trim() })} /></Row>
              <Row label={t("hw.printer.port")}><input className="field num" inputMode="numeric" value={conn.port} onChange={(e) => setConn({ ...conn, port: Number(e.target.value) || 9100 })} /></Row>
            </div>
          )}
          {conn.type === "serial" && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Row label={t("hw.printer.comPort")}>
                  <select className="field" value={conn.path} onChange={(e) => setConn({ ...conn, path: e.target.value })}>
                    <option value="">{t("hw.printer.choose")}</option>
                    {(disc?.serial ?? []).map((s) => <option key={s.path} value={s.path}>{s.label}{s.bluetooth ? " 📶 Bluetooth" : ""}</option>)}
                  </select>
                </Row>
                <Row label={t("hw.printer.baud")}><select className="field num" value={conn.baudRate} onChange={(e) => setConn({ ...conn, baudRate: Number(e.target.value) })}>{BAUDS.map((b) => <option key={b} value={b}>{b}</option>)}</select></Row>
              </div>
              <div className="rounded-2xl bg-gray-50 p-3 text-[11px] font-semibold leading-relaxed text-gray-600">
                <b className="text-cosmic-navy">{t("hw.printer.btTitle")}</b>
                <ol className="ms-4 mt-1 list-decimal space-y-0.5"><li>{t("hw.printer.bt1")}</li><li>{t("hw.printer.bt2")}</li><li>{t("hw.printer.bt3")}</li></ol>
                <button type="button" className="btn-ghost mt-2" onClick={() => void guard("bt", () => bridge.openBluetoothSettings())}>📶 {t("hw.printer.openBluetooth")}</button>
              </div>
            </div>
          )}
          {conn.type === "usb" && (
            <div className="space-y-2">
              <Row label={t("hw.printer.usbDevice")}>
                <select className="field" value={`${conn.vendorId}:${conn.productId}`} onChange={(e) => { const [v, pr] = e.target.value.split(":").map(Number); setConn({ type: "usb", vendorId: v ?? 0, productId: pr ?? 0 }); }}>
                  <option value="0:0">{t("hw.printer.choose")}</option>
                  {(disc?.usb ?? []).map((u) => <option key={u.label} value={`${u.vendorId}:${u.productId}`}>{u.label}</option>)}
                </select>
              </Row>
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">{t("hw.printer.usbWarning")}</p>
            </div>
          )}
          {incomplete && <p className="text-[11px] font-bold text-amber-700">{t("hw.printer.incomplete")}</p>}
          {test && <p role="status" className={`rounded-xl px-3 py-2 text-xs font-bold ${test.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>{test.ok ? "✔" : "✖"} {test.message} <span className="num font-medium opacity-70">({test.ms} ms)</span></p>}
        </fieldset>

        <fieldset disabled={dis} className="grid gap-3 border-t border-gray-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
          <Row label={t("hw.printer.paper")}><select className="field" value={p.paperWidth} onChange={(e) => setPrinter({ paperWidth: Number(e.target.value) as 58 | 80 })}><option value={80}>80 mm (576 dots)</option><option value={58}>58 mm (384 dots)</option></select></Row>
          <Row label={t("hw.printer.cut")}><select className="field" value={p.cut} onChange={(e) => setPrinter({ cut: e.target.value as Printer["cut"] })}>{(["partial", "full", "none"] as const).map((c) => <option key={c} value={c}>{t(`hw.printer.cutMode.${c}`)}</option>)}</select></Row>
          <Row label={t("hw.printer.copies")}><select className="field num" value={p.copies} onChange={(e) => setPrinter({ copies: Number(e.target.value) })}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</select></Row>
          <Row label={t("hw.printer.drawerPin")}><select className="field" value={p.drawerPin} onChange={(e) => setPrinter({ drawerPin: Number(e.target.value) as 2 | 5 })}><option value={2}>Pin 2</option><option value={5}>Pin 5</option></select></Row>
          <label className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2 text-xs font-bold sm:col-span-2"><span>{t("hw.printer.openDrawer")}</span><input type="checkbox" role="switch" aria-label={t("hw.printer.openDrawer")} checked={p.openDrawerOnCashSale} onChange={(e) => setPrinter({ openDrawerOnCashSale: e.target.checked })} /></label>
          <Row label={t("hw.printer.darkness", { value: p.darkness })} hint={t("hw.printer.darknessHint")}><input type="range" min={110} max={225} step={5} className="w-full" value={p.darkness} onChange={(e) => setPrinter({ darkness: Number(e.target.value) })} aria-label={t("hw.printer.darkness", { value: p.darkness })} /></Row>
          <Row label={t("hw.printer.feed")}><input className="field num" type="number" min={1} max={10} value={p.feedLines} onChange={(e) => setPrinter({ feedLines: Math.min(10, Math.max(1, Number(e.target.value) || 4)) })} /></Row>
          <Row label={t("hw.printer.bandRows")} hint={t("hw.printer.bandHint")}><input className="field num" type="number" min={16} max={255} step={16} value={p.bandRows} onChange={(e) => setPrinter({ bandRows: Math.min(255, Math.max(16, Number(e.target.value) || 128)) })} /></Row>
        </fieldset>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost" disabled={!!busy || conn.type === "none"} onClick={() => void doTest()}>🔎 {t("hw.printer.testConnection")}</button>
            <button type="button" className="btn-dark" disabled={!!busy || conn.type === "none" || incomplete} onClick={() => void sample("test-ar")}>🧾 {t("hw.printer.testAr")}</button>
            <button type="button" className="btn-dark" disabled={!!busy || conn.type === "none" || incomplete} onClick={() => void sample("test-en")}>🧾 {t("hw.printer.testEn")}</button>
            <button type="button" className="btn-ghost" disabled={!!busy || conn.type === "none" || incomplete} onClick={() => void sample("diagnostic")}>🔤 {t("hw.printer.diagnostic")}</button>
            <button type="button" className="btn-ghost" disabled={!!busy || conn.type === "none" || incomplete} onClick={() => void sample("drawer")}>💰 {t("hw.printer.testDrawer")}</button>
          </div>
          {canEdit && <button type="button" className="btn-primary" disabled={!!busy} onClick={() => void savePrinter()}>{t("hw.save")}</button>}
        </div>
        <p className="text-[10px] font-semibold text-gray-500">{t("hw.printer.silentNote")}</p>
      </section>

      <section className="card space-y-3 p-5" aria-label={t("hw.doc.title")}>
        <SectionHeader icon="📄" title={t("hw.doc.title")} subtitle={t("hw.doc.subtitle")} />
        <fieldset disabled={dis} className="grid gap-3 sm:grid-cols-3">
          <Row label={t("hw.doc.printer")}>
            <select className="field" value={doc.deviceName ?? ""} onChange={(e) => setDoc({ deviceName: e.target.value || null })}>
              <option value="">{t("hw.doc.defaultPrinter")}</option>
              {(disc?.system ?? []).map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
          </Row>
          <Row label={t("hw.printer.paper")}><select className="field" value={doc.paper} onChange={(e) => setDoc({ paper: e.target.value as DocumentPrinterSettings["paper"] })}>{(["A4", "A5", "Letter"] as const).map((x) => <option key={x} value={x}>{x}</option>)}</select></Row>
          <label className="flex items-center justify-between gap-3 self-end rounded-xl bg-gray-50 px-3 py-2 text-xs font-bold"><span>{t("hw.doc.color")}</span><input type="checkbox" role="switch" aria-label={t("hw.doc.color")} checked={doc.color} onChange={(e) => setDoc({ color: e.target.checked })} /></label>
        </fieldset>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="btn-ghost" disabled={!!busy || !window.hardware?.printDocument} onClick={() => void printDocTest()}>🖨️ {t("hw.doc.test")}</button>
          {canEdit && <button type="button" className="btn-primary" disabled={!!busy} onClick={() => void guard("savedoc", () => bridge.save({ documents: doc }).then((c) => setCfg(c)), t("hw.saved"))}>{t("hw.save")}</button>}
        </div>
      </section>

      <FailedReceipts />
    </div>
  );
}

function StatusPill({ status }: { status: "idle" | "printing" | "ok" | "error" }) {
  const { t } = useTranslation();
  return <Pill tone={status === "error" ? "red" : status === "printing" ? "amber" : status === "ok" ? "green" : "gray"}>{t(`hw.state.${status}`)}</Pill>;
}

/** Receipts whose paper did not come out: the sale is recorded, so the cashier can print them again once the printer is back. */
export function FailedReceipts() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const hw = useHardwareSnapshot();
  const bridge = window.hardware?.settings;
  const [busy, setBusy] = useState<string | null>(null);
  if (!bridge || hw.failed.length === 0) return null;
  const act = async (id: string, fn: () => Promise<void>, ok?: string) => {
    setBusy(id);
    try { await fn(); if (ok) toast(ok, "success"); } catch (e) { toastError(e); } finally { setBusy(null); void refreshHardware(); }
  };
  return (
    <section className="card space-y-3 border-red-200 p-5" aria-label={t("hw.failed.title")}>
      <SectionHeader icon="⚠️" title={t("hw.failed.title")} subtitle={t("hw.failed.subtitle")} />
      <ul className="space-y-2">
        {hw.failed.map((j) => (
          <li key={j.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-red-50 px-3 py-2 text-xs">
            <div><b className="num font-mono">{j.receiptNo}</b> · <span className="num">{money(j.total)}</span> {t("currency")} · <span className="num text-gray-500">{formatDateTime(j.at, locale)}</span><div className="text-[10px] font-semibold text-red-700">{j.error}</div></div>
            <div className="flex gap-2">
              <button type="button" className="btn-primary" disabled={busy === j.id} onClick={() => void act(j.id, () => bridge.reprint(j.id), t("hw.failed.reprinted"))}>🖨️ {t("hw.failed.reprint")}</button>
              <button type="button" className="btn-ghost" disabled={busy === j.id} onClick={() => void act(j.id, () => bridge.dismissJob(j.id))}>{t("hw.failed.dismiss")}</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
